import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject as ObservableSubject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { REQUEST_COLORS } from 'src/app/request-v2/constants/request-colors.constants';
import { REQUEST_ICONS } from 'src/app/request-v2/constants/request-icons.constants';
import { IPermissionScope } from 'src/app/auth/auth.model';
import { userScopesSelector } from 'src/app/user/store/user.selectors';
import { State } from 'src/app/store';
import { RequestCategory } from '../../../../models/request-category.model';
import { RequestDepartment } from '../../../../models/request-department.model';
import { RequestCategoryService } from '../../../../services/request-category.service';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';
import { DEFAULT_STATUS_COLORS, DEFAULT_STATUSES } from '../../../../constants/request-status.constants';
import { DropdownItem } from '../../../../../global/components/searchable-dropdown/searchable-dropdown.component';
import { RequestDepartmentService } from '../../../../services/request-department.service';
import { CreateSubjectDialogComponent } from '../create-subject-dialog/create-subject-dialog.component';
import { RequestStatusService } from '../../../../services/request-status.service';
import { RequestStatus } from '../../../../models/request-status.model';

@Component({
    selector: 'app-category-form',
    templateUrl: './category-form.component.html',
    styleUrls: ['./category-form.component.scss'],
})
export class CategoryFormComponent implements OnInit, OnDestroy {
    @Input() categoryToEdit: RequestCategory | null = null;

    // Always use untranslated subjects by default
    @Input() useUntranslatedSubjects = true;

    @Output() cancel = new EventEmitter<void>();

    @Output() saved = new EventEmitter<RequestCategory>();

    @Output() loadingStateChange = new EventEmitter<boolean>();

    currentStep = 1;

    totalSteps = 2;

    categoryForm: FormGroup;

    forcePrivateRequestsControl = new FormControl(false);

    forceLocationControl = new FormControl(true);

    isInternal = new FormControl(false);

    isDropdownVisible = false;

    isSubmitting = false;

    formErrors: { [key: string]: boolean } = {};

    statusFormVisible = false;

    editingStatusIndex: number | null = null;

    statusForm: FormGroup;

    colorPickerVisible = false;

    private destroy$ = new ObservableSubject<void>();

    icons = REQUEST_ICONS;

    colors = REQUEST_COLORS;

    statusColors = DEFAULT_STATUS_COLORS;

    wordCounts = {
        en: 0,
        fr: 0,
    };

    currentLang = 'en';

    slaOptions: DropdownItem[] = [];

    selectedSubjects: DropdownItem[] = [];

    subjects: DropdownItem[] = [];

    loadingSubjects = false;

    subjectSearchTerm = '';

    availableDepartments: RequestDepartment[] = [];

    loadingDepartments = false;

    selectedAssigneeIds: string[] = [];

    createSubjectFormVisible = false;

    isLoading = false;

    availableStatuses: RequestStatus[] = [];

    loadingStatuses = false;

    selectedStatusId = '';

    selectedStatusIds: string[] = [];

    selectedStatusItems: DropdownItem[] = [];

    filteredAvailableStatuses: DropdownItem[] = [];

    statusTypeOptions: { value: string; label: string }[] = [];

    selectedDepartment: RequestDepartment | null = null;

    private userScopes: IPermissionScope[] = [];

    constructor(
        private fb: FormBuilder,
        private elementRef: ElementRef,
        private categoryService: RequestCategoryService,
        private departmentService: RequestDepartmentService,
        private translate: TranslateService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private statusService: RequestStatusService,
        private store: Store<State>,
    ) {
        this.initForm();
        this.initStatusForm();

        this.currentLang = this.translate.currentLang || 'en';
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            this.currentLang = event.lang;
        });
    }

    ngOnInit(): void {
        this.store
            .select(userScopesSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((scopes: IPermissionScope[]) => {
                this.userScopes = scopes || [];
            });
        // Load departments first, then populate the form after they're loaded
        this.loadingState = true;
        this.loadDepartments();
        this.loadStatuses();
        this.loadStatusTypes();

        this.initSlaOptions();

        if (this.categoryToEdit) {
            this.loadAllSubjectsAndCategory();
        } else {
            // If not editing, just load all subjects
            this.loadAllSubjects();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initForm(): void {
        this.categoryForm = this.fb.group({
            visual: this.fb.group({
                icon: [REQUEST_ICONS[0], [Validators.required]],
                color: [REQUEST_COLORS[0], [Validators.required]],
            }),
            name: this.fb.group({
                en: ['', [Validators.required, Validators.maxLength(100)]],
                fr: ['', [Validators.required, Validators.maxLength(100)]],
            }),
            description: this.fb.group({
                en: ['', [Validators.required]],
                fr: ['', [Validators.required]],
            }),
            statuses: this.fb.array([], [Validators.minLength(1)]),
            departmentId: [''],
            assigneeIds: [[]],
            categorySLA: [null, [Validators.required, Validators.min(1)]],
            subCategories: this.fb.array([]),
        });

        this.updateInitialWordCounts();

        this.categoryForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (this.categoryForm.dirty) {
                this.updateFormErrors();
            }
        });
    }

    get statuses(): FormArray {
        return this.categoryForm.get('statuses') as FormArray;
    }

    get subCategories(): FormArray {
        return this.categoryForm.get('subCategories') as FormArray;
    }

    createStatus(enName: string, frName: string, color: string, type?: string, _id?: string, isDefault = false, isDeletable = true): FormGroup {
        return this.fb.group({
            name: this.fb.group({
                en: [enName, Validators.required],
                fr: [frName, Validators.required],
            }),
            color: [color, Validators.required],
            type: [type || ''],
            _id: [_id || null],
            isDefault: [isDefault],
            isDeletable: [isDeletable],
        });
    }

    addStatus(): void {
        this.statusFormVisible = true;
        this.editingStatusIndex = null;
        this.statusForm.reset();

        setTimeout(() => {
            const saveButton = this.elementRef.nativeElement.querySelector('#status-form-save-button');
            if (saveButton) {
                saveButton.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
    }

    hideStatusForm(): void {
        this.statusFormVisible = false;
        this.editingStatusIndex = null;
    }

    editStatus(index: number): void {
        this.editingStatusIndex = index;
        const status = this.statuses.at(index);

        this.statusForm.reset();
        this.statusForm.patchValue({
            name: {
                en: status.get('name.en').value,
                fr: status.get('name.fr').value,
            },
            statusType: status.get('type').value,
        });

        this.statusFormVisible = true;
    }

    saveStatus(): void {
        if (this.statusForm.invalid) {
            this.markFormGroupTouched(this.statusForm);
            return;
        }

        const nameEn = this.statusForm.get('name.en').value.trim();
        const nameFr = this.statusForm.get('name.fr').value.trim();
        const statusType = this.statusForm.get('statusType').value || '';

        let typeId = statusType;
        if (typeof statusType === 'object' && statusType.value) {
            typeId = statusType.value;
        }

        if (this.editingStatusIndex !== null) {
            const status = this.statuses.at(this.editingStatusIndex);
            status.get('name.en').setValue(nameEn);
            status.get('name.fr').setValue(nameFr);
            status.get('type').setValue(typeId || ''); // Ensure empty string, not null

            const selectedType = this.statusTypeOptions.find((t) => t.value === typeId);
            if (selectedType) {
                const defaultStatus = this.availableStatuses.find((s) => s._id === typeId);
                if (defaultStatus && defaultStatus.color) {
                    status.get('color').setValue(defaultStatus.color);
                }
            }
        } else {
            let color = '#000000';

            const selectedType = this.statusTypeOptions.find((t) => t.value === typeId);
            if (selectedType) {
                const defaultStatus = this.availableStatuses.find((s) => s._id === typeId);
                if (defaultStatus && defaultStatus.color) {
                    color = defaultStatus.color;
                }
            }

            this.statuses.push(this.createStatus(nameEn, nameFr, color, typeId || '', undefined, false, true));
        }

        this.hideStatusForm();
    }

    removeStatus(index: number): void {
        if (this.statuses.length > 1) {
            const status = this.statuses.at(index);
            if (status && status.get('isDeletable')?.value) {
                this.statuses.removeAt(index);
                this.updateFilteredAvailableStatuses();
            }
        }
    }

    getStatusBgColor(color: string): string {
        if (color.startsWith('#')) {
            let r;
            let g;
            let b;
            if (color.length === 4) {
                r = parseInt(color[1] + color[1], 16);
                g = parseInt(color[2] + color[2], 16);
                b = parseInt(color[3] + color[3], 16);
            } else {
                r = parseInt(color.substr(1, 2), 16);
                g = parseInt(color.substr(3, 2), 16);
                b = parseInt(color.substr(5, 2), 16);
            }
            return `rgba(${r}, ${g}, ${b}, 0.3)`;
        }
        return color;
    }

    updateInitialWordCounts(): void {
        const enDescription = this.categoryForm.get('description.en')?.value || '';
        const frDescription = this.categoryForm.get('description.fr')?.value || '';

        this.wordCounts.en = this.countWords(enDescription);
        this.wordCounts.fr = this.countWords(frDescription);
    }

    updateWordCount(event: Event, field: 'en' | 'fr'): void {
        const textarea = event.target as HTMLTextAreaElement;
        const text = textarea.value;

        this.wordCounts[field] = this.countWords(text);

        if (this.wordCounts[field] > 80) {
            const words = text.split(/\s+/);
            const limitedWords = words.slice(0, 80);
            const limitedText = limitedWords.join(' ');

            this.categoryForm.get(`description.${field}`)?.setValue(limitedText, { emitEvent: false });
            this.wordCounts[field] = 80;

            setTimeout(() => {
                textarea.value = limitedText;
                textarea.selectionStart = limitedText.length;
                textarea.selectionEnd = limitedText.length;
            });
        }
    }

    getWordCount(field: 'en' | 'fr'): number {
        return this.wordCounts[field];
    }

    countWords(text: string): number {
        if (!text || text.trim() === '') {
            return 0;
        }

        return text
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent): void {
        const clickedInside = this.elementRef.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isDropdownVisible = false;
            this.colorPickerVisible = false;
        }
    }

    toggleDropdownVisibility(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.isDropdownVisible = !this.isDropdownVisible;
    }

    onIconSelect(icon: string): void {
        this.categoryForm.get('visual.icon')?.setValue(icon);
        this.isDropdownVisible = false;
    }

    onColorSelect(color: string): void {
        this.categoryForm.get('visual.color')?.setValue(color);
        this.isDropdownVisible = false;
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onNext(): void {
        this.markFormGroupTouched(this.categoryForm);

        if (this.currentStep === 1) {
            // Check only first step fields are valid
            const step1Valid =
                this.categoryForm.get('visual')?.valid && this.categoryForm.get('name')?.valid && this.categoryForm.get('description')?.valid && this.categoryForm.get('statuses')?.valid;

            if (step1Valid) {
                this.currentStep += 1;
                this.formErrors = {};
                return;
            }
        } else if (this.categoryForm.valid && this.currentStep < this.totalSteps) {
            this.currentStep += 1;
            this.formErrors = {};
            return;
        }

        this.updateFormErrors();
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach((control) => {
            control.markAsTouched();

            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else if (control instanceof FormArray) {
                control.controls.forEach((arrayControl) => {
                    if (arrayControl instanceof FormGroup) {
                        this.markFormGroupTouched(arrayControl);
                    } else {
                        arrayControl.markAsTouched();
                    }
                });
            }
        });
    }

    private updateFormErrors(): void {
        this.formErrors = {};

        if (this.categoryForm.invalid) {
            if (this.currentStep === 1) {
                const visualControl = this.categoryForm.get('visual');
                if (visualControl?.invalid) {
                    if (visualControl.get('icon')?.invalid) this.formErrors.icon = true;
                    if (visualControl.get('color')?.invalid) this.formErrors.color = true;
                }

                const nameControl = this.categoryForm.get('name');
                if (nameControl?.invalid) {
                    if (nameControl.get('en')?.invalid) this.formErrors.nameEn = true;
                    if (nameControl.get('fr')?.invalid) this.formErrors.nameFr = true;
                }

                const descriptionControl = this.categoryForm.get('description');
                if (descriptionControl?.invalid) {
                    if (descriptionControl.get('en')?.invalid) this.formErrors.descriptionEn = true;
                    if (descriptionControl.get('fr')?.invalid) this.formErrors.descriptionFr = true;
                }

                const statusesControl = this.categoryForm.get('statuses') as FormArray;
                if (statusesControl?.invalid) {
                    statusesControl.controls.forEach((statusGroup, index) => {
                        if (statusGroup.get('name.en')?.invalid) {
                            this.formErrors[`status${index}En`] = true;
                        }
                        if (statusGroup.get('name.fr')?.invalid) {
                            this.formErrors[`status${index}Fr`] = true;
                        }
                        if (statusGroup.get('color')?.invalid) {
                            this.formErrors[`statusColor${index}`] = true;
                        }
                    });
                }
            } else if (this.currentStep === 2) {
                if (this.selectedSubjects.length === 0) {
                    this.formErrors.noSubjectsSelected = true;
                }

                if (!this.categoryForm.get('categorySLA')?.value) {
                    this.formErrors.categorySLAMissing = true;
                }

                const subjectSLAsFormArray = this.categoryForm.get('subCategories') as FormArray;
                const missingSLAs = this.selectedSubjects.filter((subject) => {
                    const subjectId = subject.id?.toString() || '';
                    const index = this.findSubjectSLAIndex(subjectId);
                    if (index === -1) return true;

                    const slaValue = (subjectSLAsFormArray.at(index) as FormGroup).get('slaInDays')?.value;
                    return !slaValue;
                });

                if (missingSLAs.length > 0) {
                    this.formErrors.subjectSLAsMissing = true;
                }

                // Add validation check for missing departments
                const missingDepartments = this.selectedSubjects.filter((subject) => {
                    const subjectId = subject.id?.toString() || '';
                    const index = this.findSubjectSLAIndex(subjectId);
                    if (index === -1) return true;

                    const departmentId = (subjectSLAsFormArray.at(index) as FormGroup).get('departmentId')?.value;
                    return !departmentId;
                });

                if (missingDepartments.length > 0) {
                    this.formErrors.subjectDepartmentsMissing = true;
                }
            }
        }
    }

    onPrevious(): void {
        if (this.currentStep > 1) {
            this.currentStep -= 1;
        }
    }

    onSubmit(): void {
        this.markFormGroupTouched(this.categoryForm);
        this.updateFormErrors();

        const hasCategorySLA = !!this.categoryForm.get('categorySLA')?.value;

        if (!hasCategorySLA) {
            this.formErrors.categorySLAMissing = true;
            this.submittingState = false;
            return;
        }

        const subjectSLAsFormArray = this.categoryForm.get('subCategories') as FormArray;

        const missingSubjectSLAs = this.selectedSubjects.filter((subject) => {
            const subjectId = subject.id?.toString() || '';
            const index = this.findSubjectSLAIndex(subjectId);
            if (index === -1) return true;

            const slaValue = (subjectSLAsFormArray.at(index) as FormGroup).get('slaInDays')?.value;
            return !slaValue;
        });

        if (missingSubjectSLAs.length > 0) {
            this.formErrors.subjectSLAsMissing = true;
            this.submittingState = false;
            return;
        }

        // Add validation for department assignment
        const missingDepartments = this.selectedSubjects.filter((subject) => {
            const subjectId = subject.id?.toString() || '';
            const index = this.findSubjectSLAIndex(subjectId);
            if (index === -1) return true;

            const departmentId = (subjectSLAsFormArray.at(index) as FormGroup).get('departmentId')?.value;
            return !departmentId;
        });

        if (missingDepartments.length > 0) {
            this.formErrors.subjectDepartmentsMissing = true;
            this.submittingState = false;
            return;
        }

        if (this.categoryForm.valid && !this.submittingState) {
            this.submittingState = true;
            this.submitForm();
        } else if (this.categoryForm.invalid) {
            this.submittingState = false;
            this.showErrorMessage('request_v2.category_form.form_errors');
        }
    }

    private submitForm(): void {
        if (this.categoryForm.invalid) {
            this.markFormGroupTouched(this.categoryForm);
            this.messageService.error(this.translate.instant('request_v2.category_form.form_errors'));
            this.submittingState = false;
            return;
        }

        const formValue = this.categoryForm.getRawValue();

        const categoryData: any = {
            name: formValue.name,
            description: formValue.description,
            icon: formValue.visual.icon,
            color: formValue.visual.color,
            statuses: formValue.statuses.map((s) => ({
                _id: s._id || undefined,
                name: s.name,
                color: s.color,
                ...(s.type && s.type.trim() !== '' ? { type: s.type } : {}),
                isDefault: s.isDefault,
            })),
            departmentId: formValue.departmentId || null,
            assignedTo: formValue.assigneeIds || [],
            slaInDays: Number(formValue.categorySLA),
            forcePrivateRequests: this.forcePrivateRequestsControl.value,
            forceLocation: this.forceLocationControl.value,
            isInternal: this.isInternal.value,
            subCategories: formValue.subCategories.map((sc) => ({
                _id: sc._id || undefined,
                name: sc.name,
                slaInDays: Number(sc.slaInDays),
                departmentId: sc.departmentId || null,
            })),
        };

        this.submittingState = true;
        this.loadingState = true;

        if (this.categoryToEdit && this.categoryToEdit._id) {
            this.updateCategory(this.categoryToEdit._id, categoryData);
        } else {
            this.createCategory(categoryData);
        }
    }

    private createCategory(categoryData: any): void {
        const data = { ...categoryData, subCategories: categoryData.subCategories.map((s: any) => ({ ...s, _id: s._id?.includes('temp_') ? undefined : s._id })) };
        this.categoryService
            .createRequestCategory(data)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.submittingState = false;
                }),
            )
            .subscribe({
                next: (newCategory) => {
                    this.showSuccessMessage('request_v2.category_form.create_success');
                    this.saved.emit(newCategory);
                },
                error: (_error) => {
                    this.showErrorMessage('request_v2.category_form.create_error');
                },
            });
    }

    private updateCategory(categoryId: string, categoryData: any): void {
        const data = { ...categoryData, subCategories: categoryData.subCategories.map((s: any) => ({ ...s, _id: s._id?.includes('temp_') ? undefined : s._id })) };
        this.categoryService
            .updateRequestCategory(categoryId, data)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.submittingState = false;
                }),
            )
            .subscribe({
                next: (updatedCategory) => {
                    this.showSuccessMessage('request_v2.category_form.update_success');
                    this.saved.emit(updatedCategory);
                },
                error: (_error) => {
                    this.showErrorMessage('request_v2.category_form.update_error');
                },
            });
    }

    private showSuccessMessage(key: string): void {
        this.messageService.success(this.translate.instant(key));
    }

    private showErrorMessage(key: string): void {
        this.messageService.error(this.translate.instant(key));
    }

    hasFormErrors(): boolean {
        return Object.keys(this.formErrors).length > 0;
    }

    private initStatusForm(): void {
        this.statusForm = this.fb.group({
            name: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            statusType: ['', Validators.required],
        });
    }

    toggleColorPicker(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.colorPickerVisible = !this.colorPickerVisible;
    }

    selectStatusColor(color: string): void {
        this.statusForm.get('color')?.setValue(color);
        this.colorPickerVisible = false;
    }

    // -----------------
    // Subject handling
    // -----------------
    loadAllSubjects(): void {
        this.loadingState = true;
        this.loadingSubjects = true;
        this.categoryForm.disable();

        // Always use untranslated API
        this.categoryService
            .getAllSubjectsFromCategoriesUntranslated()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (subjects) => {
                    this.subjects = subjects.map((subject) => {
                        const name: { en: string; fr: string } = { en: '', fr: '' };
                        let label = '';
                        if (subject.name) {
                            if (typeof subject.name === 'object' && subject.name !== null) {
                                const nameObj = subject.name as any;
                                name.en = nameObj.en || '';
                                name.fr = nameObj.fr || '';
                                // Always show both languages
                                label = `${nameObj.en || ''} / ${nameObj.fr || ''}`;
                            } else if (typeof subject.name === 'string') {
                                name.en = subject.name;
                                name.fr = subject.name;
                                label = subject.name;
                            }
                        }
                        return {
                            id: subject._id,
                            value: subject._id,
                            name,
                            label,
                            object: subject,
                        };
                    });

                    this.subjects.sort((a, b) => a.label.localeCompare(b.label));

                    this.loadingSubjects = false;
                    this.loadingState = false;
                    this.categoryForm.enable();

                    if (this.categoryToEdit && this.categoryToEdit.subCategories && this.categoryToEdit.subCategories.length > 0) {
                        const subjectIds = this.categoryToEdit.subCategories.map((sub) => sub.id);
                        this.categoryForm.get('subCategories')?.setValue(subjectIds);
                        this.loadSelectedSubjects(subjectIds);
                    } else {
                        this.selectedSubjects = [];
                        if (this.categoryForm.get('subCategories')) {
                            this.categoryForm.get('subCategories').setValue([]);
                        }
                    }
                    const subjectSLAsArray = this.categoryForm.get('subCategories') as FormArray;
                    subjectSLAsArray.clear();
                },
                error: (error) => {
                    console.error('Error loading subjects:', error);
                    this.messageService.error(this.translate.instant('request_v2.category_form.error_loading_subjects'));
                    this.loadingSubjects = false;
                    this.loadingState = false;
                    this.categoryForm.enable();
                },
            });
    }

    loadAllSubjectsUntranslated(): void {
        this.loadingState = true;
        this.loadingSubjects = true;
        this.categoryForm.disable();

        this.categoryService
            .getAllSubjectsFromCategoriesUntranslated()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (subjects) => {
                    this.subjects = subjects.map((subject) => {
                        const name: { en: string; fr: string } = { en: '', fr: '' };
                        let label = '';
                        if (subject.name) {
                            if (typeof subject.name === 'object' && subject.name !== null) {
                                const nameObj = subject.name as any;
                                name.en = nameObj.en || '';
                                name.fr = nameObj.fr || '';
                                // For untranslated, show both languages
                                label = `${nameObj.en || ''} / ${nameObj.fr || ''}`;
                            } else if (typeof subject.name === 'string') {
                                name.en = subject.name;
                                name.fr = subject.name;
                                label = subject.name;
                            }
                        }
                        return {
                            id: subject._id,
                            value: subject._id,
                            name,
                            label,
                            object: subject,
                        };
                    });

                    this.subjects.sort((a, b) => a.label.localeCompare(b.label));

                    this.loadingSubjects = false;
                    this.loadingState = false;
                    this.categoryForm.enable();

                    if (this.categoryToEdit && this.categoryToEdit.subCategories && this.categoryToEdit.subCategories.length > 0) {
                        const subjectIds = this.categoryToEdit.subCategories.map((sub) => sub.id);
                        this.categoryForm.get('subCategories')?.setValue(subjectIds);
                        this.loadSelectedSubjects(subjectIds);
                    } else {
                        this.selectedSubjects = [];
                        if (this.categoryForm.get('subCategories')) {
                            this.categoryForm.get('subCategories').setValue([]);
                        }
                    }
                    const subjectSLAsArray = this.categoryForm.get('subCategories') as FormArray;
                    subjectSLAsArray.clear();
                },
                error: (error) => {
                    console.error('Error loading untranslated subjects:', error);
                    this.messageService.error(this.translate.instant('request_v2.category_form.error_loading_subjects'));
                    this.loadingSubjects = false;
                    this.loadingState = false;
                    this.categoryForm.enable();
                },
            });
    }

    ensureSelectedSubjectsRemain(selectedIds: string[]): void {
        const missingSubjects = this.selectedSubjects.filter(
            (selectedSubject) => !this.subjects.some((subject) => subject.id === selectedSubject.id) && selectedIds.includes(String(selectedSubject.id)),
        );

        if (missingSubjects.length > 0) {
            this.subjects = [...this.subjects, ...missingSubjects];
        }
    }

    loadSelectedSubjects(subjectIds: string[]): void {
        if (!subjectIds || !subjectIds.length) return;

        if (this.loadingSubjects) {
            setTimeout(() => this.loadSelectedSubjects(subjectIds), 300);
            return;
        }

        const foundSubjects = this.subjects.filter((subject) => typeof subject.id === 'string' && subjectIds.includes(subject.id));

        if (foundSubjects.length < subjectIds.length && !this.loadingSubjects) {
            const foundIds = foundSubjects.map((subject) => subject.id.toString());
            const missingIds = subjectIds.filter((id) => !foundIds.includes(id));

            this.fetchMissingSubjects(missingIds, () => {
                this.selectedSubjects = this.subjects.filter((subject) => typeof subject.id === 'string' && subjectIds.includes(subject.id));
                this.categoryForm.markAsDirty();
            });
        } else {
            this.selectedSubjects = foundSubjects;
            this.categoryForm.markAsDirty();
        }
    }

    private fetchMissingSubjects(subjectIds: string[], callback?: () => void): void {
        if (!subjectIds.length) {
            if (callback) callback();
            return;
        }

        this.loadingSubjects = true;
        this.loadingState = true;
        this.categoryForm.disable(); // Disable form while loading

        // Use untranslated API for consistency
        this.categoryService
            .getAllSubjectsFromCategoriesUntranslated()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (subjects) => {
                    // Filter to just the subjects we need
                    const missingSubjects = subjects.filter((subject) => subjectIds.includes(subject._id) && !this.subjects.some((s) => s.id === subject._id));

                    // Process and add new subjects
                    const newSubjects = missingSubjects.map((subject) => {
                        // Handle both object name and string name formats
                        const name: { en: string; fr: string } = {
                            en: '',
                            fr: '',
                        };

                        // Safely handle name property
                        if (subject.name) {
                            if (typeof subject.name === 'object' && subject.name !== null) {
                                // Handle object name format
                                const nameObj = subject.name as any;
                                name.en = nameObj.en || '';
                                name.fr = nameObj.fr || '';
                            } else if (typeof subject.name === 'string') {
                                // Handle string name format
                                name.en = subject.name;
                                name.fr = subject.name;
                            }
                        }

                        // Handle label creation showing both languages
                        let label = '';
                        if (subject.name) {
                            if (typeof subject.name === 'object' && subject.name !== null) {
                                const nameObj = subject.name as any;
                                label = `${nameObj.en || ''} / ${nameObj.fr || ''}`;
                            } else if (typeof subject.name === 'string') {
                                label = subject.name;
                            }
                        }

                        return {
                            id: subject._id,
                            value: subject._id,
                            name,
                            label,
                        };
                    });

                    if (newSubjects.length) {
                        this.subjects = [...this.subjects, ...newSubjects];
                    }

                    this.loadingSubjects = false;
                    this.loadingState = false;
                    this.categoryForm.enable();

                    if (callback) callback();
                },
                error: (_error) => {
                    this.loadingSubjects = false;
                    this.loadingState = false;
                    this.categoryForm.enable();
                    if (callback) callback();
                },
            });
    }

    toggleSubjectSelection(subjectDropDownItem: DropdownItem): void {
        const subjectId = subjectDropDownItem.id;
        const subCategoriesArray = this.categoryForm.get('subCategories') as FormArray;
        const subjectIndexInSelected = this.selectedSubjects.findIndex((s) => s.id === subjectId);
        const formGroupIndex = subCategoriesArray.controls.findIndex((ctrl) => ctrl.get('_id')?.value === subjectId);

        if (subjectIndexInSelected > -1) {
            // Subject is currently selected, so deselect it
            this.selectedSubjects.splice(subjectIndexInSelected, 1);
            if (formGroupIndex > -1) {
                subCategoriesArray.removeAt(formGroupIndex);
            }
        } else {
            // Subject is not selected, so select it
            this.selectedSubjects.push(subjectDropDownItem);
            if (formGroupIndex === -1) {
                // Only add if not already in form array (should be typical)
                const newFormGroup = this.createSubCategoryFormGroup(subjectDropDownItem.object || subjectDropDownItem);

                // Set a default SLA if categorySLA has a value
                const categorySlaValue = this.categoryForm.get('categorySLA').value;
                if (categorySlaValue && !newFormGroup.get('slaInDays').value) {
                    newFormGroup.get('slaInDays')?.setValue(categorySlaValue);
                }
                subCategoriesArray.push(newFormGroup);
            }
        }

        this.categoryForm.markAsDirty();
    }

    removeSubject(subject: DropdownItem): void {
        this.toggleSubjectSelection(subject);
    }

    showCreateSubjectForm(searchTerm?: string): void {
        this.colorPickerVisible = false;
        this.isDropdownVisible = false;

        const dialogRef = this.dialog.open(CreateSubjectDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            panelClass: 'create-subject-dialog-container',
            autoFocus: false,
            data: {
                searchTerm,
                categoryId: this.categoryToEdit?._id,
                currentLang: this.currentLang,
                departments: this.availableDepartments,
                departmentId: this.categoryForm.get('departmentId').value,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    const newSubject = {
                        ...result,
                        id: result.id || result._id,
                    };

                    this.subjects = [newSubject, ...this.subjects];
                    this.toggleSubjectSelection(newSubject);
                }
            });
    }

    loadDepartments(): void {
        this.loadingDepartments = true;
        this.departmentService
            .getRequestDepartments()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loadingDepartments = false;
                    if (this.categoryToEdit && !this.isLoading) {
                        this.loadSelectedDepartment(this.categoryToEdit.department._id);
                    }
                }),
            )
            .subscribe({
                next: (departments) => {
                    this.availableDepartments = departments.map((department) => ({
                        ...department,
                        value: department._id,
                        label: department.name || department._id,
                    }));
                },
                error: (error) => {},
            });
    }

    loadSelectedDepartment(departmentId: string): void {
        if (!departmentId) return;

        this.selectedDepartment = this.availableDepartments.find((dept) => dept._id === departmentId) || null;

        if (!this.selectedDepartment && !this.loadingDepartments) {
            this.departmentService
                .getRequestDepartmentById(departmentId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (department) => {
                        if (department) {
                            const formattedDept = {
                                ...department,
                                value: department._id,
                                label: department.name || department._id,
                            };
                            if (!this.availableDepartments.some((d) => d._id === department._id)) {
                                this.availableDepartments = [...this.availableDepartments, formattedDept];
                            }
                            this.selectedDepartment = formattedDept;

                            setTimeout(() => {
                                this.categoryForm.get('departmentId')?.setValue(departmentId);
                                if (department.managers && department.managers.length > 0) {
                                    const managerIds = department.managers.map((manager) => (typeof manager === 'string' ? manager : manager._id));
                                    this.selectedAssigneeIds = managerIds;
                                    this.categoryForm.get('assigneeIds')?.setValue(managerIds);
                                }
                            });
                        }
                    },
                    (_error) => {
                        console.error('Error in loadSelectedDepartment', _error);
                    },
                );
        } else if (this.selectedDepartment) {
            if (this.selectedDepartment.managers && this.selectedDepartment.managers.length > 0) {
                const managerIds = this.selectedDepartment.managers.map((manager) => (typeof manager === 'string' ? manager : manager._id));
                this.selectedAssigneeIds = managerIds;
                this.categoryForm.get('assigneeIds')?.setValue(managerIds);
            }
        }

        setTimeout(() => {
            this.categoryForm.get('departmentId')?.setValue(departmentId);
        });
    }

    onDepartmentChange(selectedDepartment: any): void {
        if (selectedDepartment) {
            this.selectedDepartment = this.availableDepartments.find((dept) => dept._id === selectedDepartment.value) || null;
            this.categoryForm.get('departmentId')?.setValue(selectedDepartment.value);

            if (this.selectedDepartment?.managers && this.selectedDepartment.managers.length > 0) {
                const managerIds = this.selectedDepartment.managers.map((manager) => {
                    if (typeof manager === 'string') {
                        return manager;
                    }
                    return manager._id;
                });
                this.selectedAssigneeIds = managerIds;
                this.categoryForm.get('assigneeIds')?.setValue(managerIds);
            }
        } else {
            this.selectedDepartment = null;
            this.categoryForm.get('departmentId')?.setValue(null);
            this.selectedAssigneeIds = [];
            this.categoryForm.get('assigneeIds')?.setValue([]);
        }
    }

    onAssigneeChange(assignees: any[]): void {
        const assigneeIds = assignees.map((assignee) => assignee.id || assignee.value || assignee);
        this.selectedAssigneeIds = assigneeIds;
        this.categoryForm.get('assigneeIds')?.setValue(assigneeIds);

        this.categoryForm.get('assigneeIds')?.updateValueAndValidity();
    }

    prepareColorItems(): { value: string; label: string; style: string }[] {
        return this.colors.map((color) => ({
            value: color,
            label: color,
            style: `background-color: ${color}`,
        }));
    }

    private initSlaOptions(): void {
        this.slaOptions = Array.from({ length: 30 }, (_, i) => ({
            id: (i + 1).toString(),
            value: i + 1,
            label: `${i + 1} ${this.translate.instant('request_v2.category_form.days')}`,
        }));
    }

    onCategorySlaChange(event: any): void {
        if (!event) return;

        const selectedSla = typeof event === 'object' ? event.value : event;
        const numericSla = Number(selectedSla);
        this.categoryForm.get('categorySLA')?.setValue(numericSla);

        this.updateEmptySubjectSLAs(numericSla);
    }

    private updateEmptySubjectSLAs(categorySlaValue: number): void {
        if (!this.selectedSubjects || this.selectedSubjects.length === 0) return;

        const subCategoriesArray = this.categoryForm.get('subCategories') as FormArray;

        this.selectedSubjects.forEach((subjectDropdownItem) => {
            const subjectId = subjectDropdownItem.id?.toString() || '';
            if (!subjectId) return;

            const existingIndex = this.findSubjectSLAIndex(subjectId);

            if (existingIndex > -1) {
                const subjectFormGroup = subCategoriesArray.at(existingIndex) as FormGroup;
                const currentSLAValue = subjectFormGroup.get('slaInDays')?.value;

                if (!currentSLAValue) {
                    // Only update if it's empty or explicitly needs to be overwritten
                    subjectFormGroup.get('slaInDays')?.setValue(categorySlaValue);
                }
            }
        });
    }

    onSubjectSlaChange(subject: DropdownItem, event: any): void {
        if (!subject || !event) return;

        const selectedSla = typeof event === 'object' ? event.value : event;
        const subjectId = subject.id?.toString() || '';

        this.updateSubjectSLA(subjectId, selectedSla);
    }

    private updateSubjectSLA(subjectId: string, slaDays: number): void {
        if (!subjectId) return;

        const subjectSLAs = this.categoryForm.get('subCategories') as FormArray;
        const existingIndex = this.findSubjectSLAIndex(subjectId);

        if (existingIndex > -1) {
            (subjectSLAs.at(existingIndex) as FormGroup).get('slaInDays')?.setValue(slaDays);
        } else {
            // This case should ideally not be hit if toggleSubjectSelection correctly adds the FormGroup
            // If it is hit, it means we are trying to set an SLA for a subject not yet in the form.
            // Consider if you need to add the full subject FormGroup here.
            // For now, let's find the subject data from selectedSubjects to create the FormGroup.
            const subjectData = this.selectedSubjects.find((s) => s.id === subjectId);
            if (subjectData && subjectData.object) {
                const newFormGroup = this.createSubCategoryFormGroup(subjectData.object);
                newFormGroup.get('slaInDays')?.setValue(slaDays);
                subjectSLAs.push(newFormGroup);
            } else {
                console.warn(`Cannot update/add SLA for subject ID ${subjectId} as it was not found in selectedSubjects or lacks object data.`);
            }
        }
    }

    private findSubjectSLAIndex(subjectId: string): number {
        const subCategoriesArray = this.categoryForm.get('subCategories') as FormArray;
        // Ensure this matches the ID field used in createSubCategoryFormGroup (which is _id)
        return subCategoriesArray.controls.findIndex((control) => control.get('_id')?.value === subjectId);
    }

    getSubjectSLAValue(subjectId: string | number): string | number {
        if (!subjectId) return '';

        const subjectSLAs = this.categoryForm.get('subCategories') as FormArray;
        const index = this.findSubjectSLAIndex(subjectId.toString());

        if (index === -1) return '';

        return (subjectSLAs.at(index) as FormGroup).get('slaInDays')?.value || '';
    }

    hasSubjectDepartment(subjectId: string | number): boolean {
        if (!subjectId) return false;

        const subjectSLAs = this.categoryForm.get('subCategories') as FormArray;
        const index = this.findSubjectSLAIndex(subjectId.toString());

        if (index === -1) return false;

        const departmentId = (subjectSLAs.at(index) as FormGroup).get('departmentId')?.value;
        return !!departmentId;
    }

    private populateForm(): void {
        if (!this.categoryToEdit) return;

        this.isLoading = true;
        this.loadingState = true;

        this.categoryForm.patchValue({
            visual: {
                icon: this.categoryToEdit.icon || REQUEST_ICONS[0],
                color: this.categoryToEdit.color || REQUEST_COLORS[0],
            },
            name: {
                en: typeof this.categoryToEdit.name === 'object' ? this.categoryToEdit.name.en : this.categoryToEdit.name,
                fr: typeof this.categoryToEdit.name === 'object' ? this.categoryToEdit.name.fr : this.categoryToEdit.name,
            },
            description: {
                en: typeof this.categoryToEdit.description === 'object' ? this.categoryToEdit.description.en : this.categoryToEdit.description,
                fr: typeof this.categoryToEdit.description === 'object' ? this.categoryToEdit.description.fr : this.categoryToEdit.description,
            },
            departmentId: this.categoryToEdit.department?._id || '',
            assigneeIds: this.categoryToEdit.assignedTo || [],
            categorySLA: this.categoryToEdit.slaInDays !== null ? Number(this.categoryToEdit.slaInDays) : null,
        });

        this.forcePrivateRequestsControl.setValue(this.categoryToEdit.forcePrivateRequests || false);

        this.forceLocationControl.setValue(this.categoryToEdit.forceLocation === true);

        this.isInternal.setValue(this.categoryToEdit.isInternal || false);

        this.populateCategoryStatuses();

        this.updateFilteredAvailableStatuses();

        this.updateInitialWordCounts();

        if (this.categoryToEdit.subCategories && this.categoryToEdit.subCategories.length > 0) {
            const subCategoriesArray = this.categoryForm.get('subCategories') as FormArray;
            subCategoriesArray.clear();
            this.selectedSubjects = [];
            this.categoryToEdit.subCategories.forEach((subCat) => {
                const formGroup = this.createSubCategoryFormGroup(subCat);
                subCategoriesArray.push(formGroup);

                let label = '';
                if (typeof subCat.name === 'object') {
                    label = this.currentLang === 'fr' ? subCat.name.fr || subCat.name.en : subCat.name.en || subCat.name.fr;
                } else {
                    label = subCat.name;
                }
                this.selectedSubjects.push({
                    id: subCat._id || subCat.id || this.generateTemporaryId(),
                    label: label || '',
                    value: subCat._id || subCat.id,
                    object: subCat,
                });
            });
        }

        setTimeout(() => {
            this.isLoading = false;
            this.loadingState = false;
            this.categoryForm.markAsPristine();
        });
    }

    private createSubCategoryFormGroup(subCategoryData: any): FormGroup {
        let enName = '';
        let frName = '';

        if (typeof subCategoryData.name === 'string') {
            enName = subCategoryData.name;
            frName = subCategoryData.name; // Assume same for fr if only string is provided
        } else if (subCategoryData.name && typeof subCategoryData.name === 'object') {
            enName = subCategoryData.name.en || '';
            frName = subCategoryData.name.fr || enName; // Fallback fr to en if fr is missing
        }

        const effectiveId = subCategoryData.id || subCategoryData._id || this.generateTemporaryId();

        return this.fb.group({
            _id: [effectiveId], // Use the determined ID. This ensures new items use their temp ID for matching.
            name: this.fb.group({
                en: [enName, Validators.required],
                fr: [frName || enName, Validators.required], // Ensure fr also has a value, fallback to en
            }),
            slaInDays: [subCategoryData.slaInDays || null, [Validators.required, Validators.min(1)]],
            departmentId: [subCategoryData.departmentId || subCategoryData.department?._id || null, Validators.required],
        });
    }

    private generateTemporaryId(): string {
        return `temp_${Math.random().toString(36).substr(2, 9)}`;
    }

    private loadAllSubjectsAndCategory(): void {
        if (!this.categoryToEdit || !this.categoryToEdit._id) {
            this.loadAllSubjects();
            this.isLoading = false;
            this.loadingState = false;
            return;
        }

        this.isLoading = true;
        this.loadingState = true;
        this.loadingSubjects = true;

        // Always use untranslated API
        this.categoryService
            .getAllSubjectsFromCategoriesUntranslated()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loadingSubjects = false;
                    this.isLoading = false;
                    this.loadingState = false;
                }),
            )
            .subscribe({
                next: (subjects) => {
                    this.subjects = subjects.map((subject) => {
                        let label = '';
                        const nameVal: { en: string; fr: string } = { en: '', fr: '' };
                        if (subject.name) {
                            if (typeof subject.name === 'object' && subject.name !== null) {
                                // Ensure it's a non-null object
                                nameVal.en = subject.name!.en || '';
                                nameVal.fr = subject.name!.fr || '';
                                // Always show both languages
                                label = `${nameVal.en} / ${nameVal.fr}`;
                            } else if (typeof subject.name === 'string') {
                                label = subject.name;
                                nameVal.en = subject.name;
                                nameVal.fr = subject.name;
                            }
                        }
                        return {
                            id: subject._id,
                            label,
                            value: subject._id,
                            name: nameVal,
                            object: subject,
                        };
                    });

                    this.loadCategoryForEdition(this.categoryToEdit._id);
                },
                error: (_error) => {
                    this.loadingSubjects = false;
                    this.messageService.error(this.translate.instant('request_v2.category_form.error_loading_subjects'));

                    if (!this.categoryToEdit) {
                        this.addDefaultStatuses();
                    } else {
                        this.populateCategoryStatuses();
                    }
                    this.isLoading = false;
                    this.loadingState = false;
                },
            });
    }

    private addDefaultStatuses(): void {
        while (this.statuses.length) {
            this.statuses.removeAt(0);
        }

        if (this.availableStatuses.length > 0) {
            this.availableStatuses
                .filter((status) => status.isDefault)
                .forEach((status) => {
                    let nameEn = '';
                    let nameFr = '';

                    const statusName = status.name as any;
                    if (statusName && typeof statusName === 'object') {
                        nameEn = statusName.en || '';
                        nameFr = statusName.fr || '';
                    } else if (statusName && typeof statusName === 'string') {
                        nameEn = statusName;
                        nameFr = statusName;
                    }

                    const isDeletable = (status as any).isDeletable !== undefined ? (status as any).isDeletable : !status.isDefault;
                    const statusType = status.type || ''; // Ensure empty string instead of null/undefined

                    this.statuses.push(this.createStatus(nameEn, nameFr, status.color || '#808080', statusType, status._id, status.isDefault || false, isDeletable));
                });
        } else {
            DEFAULT_STATUSES.forEach((status) => {
                const isDeletable = !status.isDefault;
                const statusType = status.type || ''; // Ensure empty string instead of null/undefined

                this.statuses.push(this.createStatus(status.nameEn, status.nameFr, status.color, statusType, undefined, status.isDefault, isDeletable));
            });
        }

        this.updateFilteredAvailableStatuses();
    }

    private populateCategoryStatuses(): void {
        while (this.statuses.length) {
            this.statuses.removeAt(0);
        }

        if (this.categoryToEdit && this.categoryToEdit.statuses && Array.isArray(this.categoryToEdit.statuses) && this.categoryToEdit.statuses.length > 0) {
            this.categoryToEdit.statuses.forEach((status) => {
                let statusNameEn = '';
                let statusNameFr = '';
                const statusId = status._id;
                const isDeletable = (status as any).isDeletable !== undefined ? (status as any).isDeletable : !status.isDefault;
                const statusType = status.type || ''; // Ensure empty string instead of null/undefined

                if (typeof status.name === 'object' && status.name !== null) {
                    statusNameEn = status.name.en || '';
                    statusNameFr = status.name.fr || '';
                } else if (typeof status.name === 'string') {
                    statusNameEn = status.name;
                    statusNameFr = status.name;
                }

                this.statuses.push(this.createStatus(statusNameEn, statusNameFr, status.color || '#808080', statusType, statusId, status.isDefault || false, isDeletable));
            });
        } else if (this.availableStatuses.length > 0) {
            const defaultStatus = this.availableStatuses.find((s) => s.isDefault) || this.availableStatuses[0];
            let nameEn = '';
            let nameFr = '';
            const statusType = defaultStatus.type || ''; // Ensure empty string instead of null/undefined

            const statusName = defaultStatus.name as any;
            if (statusName && typeof statusName === 'object') {
                nameEn = statusName.en || '';
                nameFr = statusName.fr || '';
            } else if (statusName && typeof statusName === 'string') {
                nameEn = statusName;
                nameFr = statusName;
            }

            this.statuses.push(this.createStatus(nameEn, nameFr, defaultStatus.color || '#808080', statusType, defaultStatus._id, defaultStatus.isDefault || false, false));
        } else {
            const status = DEFAULT_STATUSES[0];
            const statusType = status.type || ''; // Ensure empty string instead of null/undefined

            this.statuses.push(this.createStatus(status.nameEn, status.nameFr, status.color, statusType, undefined, status.isDefault, false));
        }
    }

    getSelectedStatusIds(): string[] {
        const selectedIds: string[] = [];

        for (let i = 0; i < this.statuses.length; i += 1) {
            const statusControl = this.statuses.at(i);
            const id = statusControl.get('_id')?.value;
            if (id) {
                selectedIds.push(id.toString());
            }
        }

        return selectedIds;
    }

    removeStatusById(statusId: string): void {
        if (!statusId) {
            return;
        }

        const statusIdStr = statusId.toString();

        for (let i = 0; i < this.statuses.length; i += 1) {
            const status = this.statuses.at(i);
            const formStatusId = status.get('_id')?.value;

            if (formStatusId && formStatusId.toString() === statusIdStr) {
                const isDeletable = status.get('isDeletable')?.value;

                if (isDeletable) {
                    this.statuses.removeAt(i);
                    break;
                }
            }
        }
    }

    updateFilteredAvailableStatuses(): void {
        const selectedIds = this.getSelectedStatusIds();
        this.selectedStatusIds = [...selectedIds];

        this.filteredAvailableStatuses = this.availableStatuses.map((status) => {
            const isSelected = this.isStatusSelected(status._id);

            let name: any;
            if (status.name) {
                if (typeof status.name === 'object' && status.name !== null) {
                    name = status.name;
                } else if (typeof status.name === 'string') {
                    name = {
                        en: status.name,
                        fr: status.name,
                    };
                }
            } else {
                name = {
                    en: '',
                    fr: '',
                };
            }

            return {
                id: status._id,
                value: status._id,
                label: this.getStatusLabel(status),
                name,
                isSelected,
                color: status.color,
                isDefault: status.isDefault,
                isDeletable: (status as any).isDeletable,
            };
        });

        this.updateSelectedStatusItems();
    }

    updateSelectedStatusItems(): void {
        const selectedIdStrings = this.selectedStatusIds.map((id) => id.toString());

        this.selectedStatusItems = this.filteredAvailableStatuses.filter((item) => {
            const itemId = item.id ? item.id.toString() : '';
            const itemValue = item.value ? item.value.toString() : '';
            const isSelected = selectedIdStrings.includes(itemId) || selectedIdStrings.includes(itemValue);

            return isSelected;
        });
    }

    onItemSelect(item: DropdownItem): void {
        if (!item || (!item.id && !item.value)) return;

        const statusId = (item.id || item.value).toString();

        if (!this.isStatusSelected(statusId)) {
            const selectedStatus = this.availableStatuses.find((s) => s._id === statusId);
            if (selectedStatus) {
                let nameEn = '';
                let nameFr = '';

                const statusName = selectedStatus.name as any;
                if (statusName && typeof statusName === 'object') {
                    nameEn = statusName.en || '';
                    nameFr = statusName.fr || '';
                } else if (statusName && typeof statusName === 'string') {
                    nameEn = statusName;
                    nameFr = statusName;
                }

                const isDeletable = (selectedStatus as any).isDeletable !== undefined ? (selectedStatus as any).isDeletable : !selectedStatus.isDefault;
                const statusType = selectedStatus.type || ''; // Ensure empty string instead of null/undefined

                this.statuses.push(this.createStatus(nameEn, nameFr, selectedStatus.color || '#808080', statusType, selectedStatus._id, selectedStatus.isDefault || false, isDeletable));

                this.updateFilteredAvailableStatuses();
            }
        }
    }

    onItemDeselect(item: DropdownItem): void {
        if (!item || (!item.id && !item.value)) return;

        const statusId = (item.id || item.value).toString();

        this.removeStatusById(statusId);

        this.updateFilteredAvailableStatuses();
    }

    getStatusLabel(status: any): string {
        if (status.name) {
            if (typeof status.name === 'object' && status.name !== null) {
                return `${status.name.en || ''} / ${status.name.fr || ''}`;
            }
            if (typeof status.name === 'string') {
                return status.name;
            }
        }
        return '';
    }

    isStatusSelected(statusId: string): boolean {
        if (!statusId) return false;

        const statusIdStr = statusId.toString();

        for (let i = 0; i < this.statuses.length; i += 1) {
            const status = this.statuses.at(i);
            const statusFormId = status.get('_id')?.value;

            if (statusFormId && statusFormId.toString() === statusIdStr) {
                return true;
            }
        }

        return false;
    }

    onStatusSelect(event: any): void {
        if (!event || !event.value) return;

        const newSelectedIds = Array.isArray(event.value) ? event.value : [event.value];

        const currentSelectedIds = this.getSelectedStatusIds();

        const idsToAdd = newSelectedIds.filter((id) => !currentSelectedIds.includes(id));

        const idsToRemove = currentSelectedIds.filter((id) => !newSelectedIds.includes(id));

        for (const statusId of idsToAdd) {
            const selectedStatus = this.availableStatuses.find((s) => s._id === statusId);
            if (selectedStatus) {
                let nameEn = '';
                let nameFr = '';

                const statusName = selectedStatus.name as any;
                if (statusName && typeof statusName === 'object') {
                    nameEn = statusName.en || '';
                    nameFr = statusName.fr || '';
                } else if (statusName && typeof statusName === 'string') {
                    nameEn = statusName;
                    nameFr = statusName;
                }

                const isDeletable = (selectedStatus as any).isDeletable !== undefined ? (selectedStatus as any).isDeletable : !selectedStatus.isDefault;

                this.statuses.push(this.createStatus(nameEn, nameFr, selectedStatus.color || '#808080', selectedStatus.type, selectedStatus._id, selectedStatus.isDefault || false, isDeletable));
            }
        }

        for (const statusId of idsToRemove) {
            this.removeStatusById(statusId);
        }

        this.updateFilteredAvailableStatuses();
    }

    private loadStatuses(): void {
        this.loadingStatuses = true;
        // Load both default statuses and organization-specific statuses
        const defaultStatuses$ = this.statusService.getDefaultStatusList();
        const orgStatuses$ = this.statusService.getAllCreatedByOrg(); // Assuming this method exists on statusService

        // Use forkJoin to load both simultaneously
        forkJoin({
            defaultStatuses: defaultStatuses$,
            orgStatuses: orgStatuses$,
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.loadingStatuses = false;
                    if (!this.categoryToEdit) {
                        this.addDefaultStatuses();
                    } else {
                        this.populateCategoryStatuses();
                    }
                }),
            )
            .subscribe({
                next: ({ defaultStatuses, orgStatuses }) => {
                    // Merge the two arrays, avoiding duplicates based on _id
                    const mergedStatuses = [...defaultStatuses];

                    // Add org statuses that don't already exist in default statuses
                    orgStatuses.forEach((orgStatus) => {
                        const existsInDefault = defaultStatuses.some((defaultStatus) => defaultStatus._id === orgStatus._id);
                        if (!existsInDefault) {
                            mergedStatuses.push({
                                ...orgStatus,
                                isDeletable: orgStatus.isDeletable !== undefined ? orgStatus.isDeletable : true, // Org-created statuses are typically deletable
                            });
                        }
                    });

                    this.availableStatuses = mergedStatuses.map((s) => ({
                        ...s,
                        isDeletable: s.isDeletable !== undefined ? s.isDeletable : !s.isDefault,
                    }));
                    this.updateFilteredAvailableStatuses();
                },
                error: (_error) => {
                    this.messageService.error(this.translate.instant('request_v2.category_form.error_loading_statuses'));
                    if (!this.categoryToEdit) {
                        this.addDefaultStatuses();
                    }
                },
            });
    }

    private loadCategoryForEdition(categoryId: string): void {
        this.categoryService
            .getRequestCategoryById(categoryId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (category) => {
                    if (category) {
                        this.categoryToEdit = category;
                        this.populateForm();
                    } else {
                        this.messageService.error(this.translate.instant('request_v2.category_form.load_category_error'));
                    }
                    this.isLoading = false;
                    this.loadingState = false;
                },
                error: (_error) => {
                    this.messageService.error(this.translate.instant('request_v2.category_form.load_category_error'));
                    this.isLoading = false;
                    this.loadingState = false;
                },
            });
    }

    set submittingState(value: boolean) {
        this.isSubmitting = value;
        this.loadingStateChange.emit(value);
    }

    get submittingState(): boolean {
        return this.isSubmitting;
    }

    set loadingState(value: boolean) {
        this.isLoading = value;
        this.loadingStateChange.emit(value);
    }

    get loadingState(): boolean {
        return this.isLoading;
    }

    private loadStatusTypes(): void {
        const currentLang = this.translate.currentLang || this.translate.defaultLang || 'en';

        this.statusService
            .getDefaultStatusList()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (statusTypes) => {
                    if (!statusTypes || statusTypes.length === 0) {
                        return;
                    }

                    const options = [];
                    statusTypes.forEach((status) => {
                        if (!status) return;

                        try {
                            let labelText = '';
                            if (status.name) {
                                if (typeof status.name === 'object') {
                                    labelText = status.name![currentLang] || '';
                                } else if (typeof status.name === 'string') {
                                    labelText = status.name;
                                }
                            }

                            options.push({
                                value: status._id,
                                label: labelText || status._id,
                            });
                        } catch (_err) {
                            // Skip invalid status
                        }
                    });

                    this.statusTypeOptions = options;
                },
                error: (_err) => {
                    this.messageService.error(this.translate.instant('request_v2.status_form.status_type_load_error'));
                    this.statusTypeOptions = [];
                },
            });
    }

    onSubCategoryDepartmentChange(_subCategoryControl: any, _newDepartmentId: string | null): void {
        // The form control value (subCategoryControl.get('departmentId').value) should already be updated by Angular Forms.
        // This function can be used for logging, or if other parts of the UI
        // not directly bound to this form control need to react.
        this.categoryForm.markAsDirty();
        // Example: If you need to access the control directly:
        // const departmentIdControl = subCategoryControl.get('departmentId');
        // if (departmentIdControl) {
        //   console.log('Department ID changed to:', departmentIdControl.value);
        // }
    }
}
